import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getItemListClick, getValueBinding } from '../../shared';
import { AudioPlayerProps } from '../type';
import { Player } from './Player';
import { showAlert } from '@common/utils/handleActions/func/func';
import { downloadFile as downloadFileAction } from '@common/utils/handleActions/ActionItem';

export const PlayerPreview: React.FC<AudioPlayerProps> = (props) => {
  const { onAudioPlayed, onAudioFinished } = props.attributes;
  const bindingValue = getValueBinding(
    props.record ? props.initId : props.id,
    props.record ? props?.record : props.data,
    props
  );
  const url = bindingValue?.url?.toString() || '';
  const title = bindingValue?.title?.toString() || 'Audio Player';
  const autoPlay = bindingValue?.autoPlay?.toString() === 'true';
  const [duration, setDuration] = useState(120);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!url) {
      return;
    }
    const a = audioRef.current;
    if (!a) {
      return;
    }

    const onLoadedMeta = (e: any) => {
      setDuration(Math.ceil(e.target.duration));
    };
    const onTimeUpdate = (e: any) => {
      setCurrentTime(Math.ceil(e.target.currentTime));
    };
    const onPlay = (e: any) => {
      if (onAudioPlayed) {
        props.onPress?.(onAudioPlayed?.actionId, {
          itemListClick: getItemListClick(props.record),
        });
      }
      setIsPlaying(true);
    };
    const onPause = () => {};
    const onEndedInternal = () => {
      if (onAudioFinished) {
        props.onPress?.(onAudioFinished?.actionId, {
          itemListClick: getItemListClick(props.record),
        });
      }
      setIsPlaying(false);
    };

    a.addEventListener('loadedmetadata', onLoadedMeta);
    a.addEventListener('timeupdate', onTimeUpdate);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEndedInternal);

    return () => {
      a.removeEventListener('loadedmetadata', onLoadedMeta);
      a.removeEventListener('timeupdate', onTimeUpdate);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEndedInternal);
    };
  }, [url, audioRef]);

  const togglePlay = async () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const onSlide = useCallback(
    (value: number) => {
      if (audioRef.current) {
        const wasPlaying = !audioRef.current.paused;
        audioRef.current.pause();
        audioRef.current.currentTime = value;
        if (wasPlaying) {
          setTimeout(() => {
            audioRef.current?.play();
          }, 300);
        }
      }
    },
    [audioRef.current, duration]
  );

  const seekForward = useCallback(() => {
    onSlide(Math.min(duration, currentTime + 5));
  }, [currentTime, duration, onSlide]);

  const seekBackward = useCallback(() => {
    onSlide(Math.max(0, currentTime - 5));
  }, [currentTime, onSlide]);

  const downloadFile = async () => {
    await downloadFileAction({
      response: {
        url: url,
        fileName: url.split('/').pop()?.split('?')[0] || '',
      },
    });
  };
  // Most browsers block any audio that is played by a web page and is not related to user interaction (see https://developer.mozilla.org/en-US/docs/Web/Media/Autoplay_guide).
  return (
    <div id={props.id}>
      <audio ref={audioRef} autoPlay={autoPlay} preload="metadata">
        <source src={url} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      <Player
        {...props}
        onDownload={downloadFile}
        onPlayPause={togglePlay}
        onSeek={onSlide}
        onSeekBackward={seekBackward}
        onSeekForward={seekForward}
        time={currentTime}
        duration={duration}
        isPlaying={isPlaying}
        title={title}
      />
    </div>
  );
};
